import type { Mat3 } from '../core/math';
import type { Point } from '../core/types';
import type { StrokeTargetKind } from '../engine/strokeTarget';
import type { Texture } from '../gl/texture';

/**
 * Frame-local render state that must not live in the React store, because it
 * holds GPU objects and changes at pointer-event rate. Tools mutate it directly
 * and then ask the engine for a redraw.
 */
export interface LiveRenderState {
  /** Textures that replace a layer's own pixels for this frame. */
  overrides: Map<string, Texture>;
  /** Textures that replace a layer's mask, while a stroke paints into it. */
  maskOverrides: Map<string, Texture>;
  /** Textures that replace an alpha channel, while a stroke paints into it. */
  channelOverrides: Map<string, Texture>;
  /** Live move / free-transform preview. */
  transform: { layerId: string; srcMatrix: Mat3 } | null;
  /** Transform Selection preview: warps the outline, not the pixels. */
  selectionTransform: Mat3 | null;
  /** Dashed-outline animation offset, in screen pixels. */
  antsPhase: number;
  showSelection: boolean;
  /** Tip outline follows the pointer while a paint tool is active. */
  brushCursor: { point: Point; radius: number; roundness: number; angle: number } | null;
}

export const liveRender: LiveRenderState = {
  overrides: new Map(),
  maskOverrides: new Map(),
  channelOverrides: new Map(),
  transform: null,
  selectionTransform: null,
  antsPhase: 0,
  showSelection: true,
  brushCursor: null,
};

/** Routes a stroke preview to the pixels, the mask or an alpha channel. */
export function setLivePreview(
  kind: StrokeTargetKind,
  id: string,
  texture: Texture | null,
): void {
  const map =
    kind === 'mask'
      ? liveRender.maskOverrides
      : kind === 'channel'
        ? liveRender.channelOverrides
        : liveRender.overrides;
  if (texture) map.set(id, texture);
  else map.delete(id);
}

export function clearLiveOverrides(): void {
  liveRender.overrides.clear();
  liveRender.maskOverrides.clear();
  liveRender.channelOverrides.clear();
  liveRender.transform = null;
  liveRender.selectionTransform = null;
}
